import { isAxiosError } from 'axios';
import { isSessionValid } from '@/helpers/auth';
import type { Session } from '@ory/client';

interface BrowserLoginFlowRequest {
  refresh: true;
  returnTo: string;
  organization: string;
  aal?: 'aal1';
}

export const getSafeLoginReturnTo = (
  returnTo: string | undefined,
  origin: string,
  tenantId: string,
): string => {
  const fallback = `${origin}/${tenantId}`;
  if (!returnTo) {
    return fallback;
  }

  try {
    const url = new URL(returnTo, origin);
    if (url.origin !== origin) {
      return fallback;
    }
    if (/\/login(?:[/?#]|$)/.test(url.pathname)) {
      return fallback;
    }
    return url.toString();
  } catch {
    return fallback;
  }
};

/**
 * Path to land on after picking a profile, or undefined to fall back to the
 * tenant dashboard. Only pages inside the profile's own tenant qualify, so a
 * stale `returnTo` cannot carry the user into another tenant or off-site.
 */
export const getProfileReturnPath = (
  returnTo: string | undefined,
  origin: string,
  tenantId: string,
): string | undefined => {
  if (!returnTo) {
    return undefined;
  }

  try {
    const url = new URL(returnTo, origin);
    if (url.origin !== origin) {
      return undefined;
    }
    const [, firstSegment, ...rest] = url.pathname.split('/');
    if (firstSegment !== tenantId || rest.join('/') === '') {
      return undefined;
    }
    if (/\/log(?:in|out)(?:[/?#]|$)/.test(url.pathname)) {
      return undefined;
    }
    return `${url.pathname}${url.search}${url.hash}`;
  } catch {
    return undefined;
  }
};

export const isExistingOrySession = (
  session: Session | undefined,
  whoamiError: unknown,
): boolean => {
  if (isSessionValid(session)) {
    return true;
  }

  // whoami returns 403 when a session cookie exists but does not meet the
  // required AAL (e.g. 2FA enrolled, session still aal1). Kratos still
  // treats that cookie as a live session for login-flow init.
  return isAxiosError(whoamiError) && whoamiError.response?.status === 403;
};

export const buildBrowserLoginFlowRequest = ({
  returnTo,
  organization,
  sessionExists,
}: {
  returnTo: string;
  organization: string;
  sessionExists: boolean;
}): BrowserLoginFlowRequest => {
  // Always send refresh: Kratos ignores it when no session exists, and
  // rejects with session_already_available (HTTP 400) when one does.
  const request: BrowserLoginFlowRequest = {
    refresh: true,
    returnTo,
    organization,
  };

  if (sessionExists) {
    request.aal = 'aal1';
  }

  return request;
};

export const getOryErrorId = (error: unknown): string | undefined => {
  if (!isAxiosError(error)) {
    return undefined;
  }

  const data = error.response?.data;
  if (
    typeof data === 'object' &&
    data !== null &&
    'error' in data &&
    typeof data.error === 'object' &&
    data.error !== null &&
    'id' in data.error &&
    typeof data.error.id === 'string'
  ) {
    return data.error.id;
  }

  return undefined;
};
